import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {StateData} from "../../fstn/state";
import {GridSelectionModel} from "@mui/x-data-grid-pro";

export interface ExceptionType {
    message: string,
    stack: string[],
    node: string
}
export interface StateUpdate {
    state: string
    data: StateData
}
export interface IAssetsData {
}
export interface IGraphData {
    events: string[]
    graphLayout: string
    lockedStates: boolean
    quickViewEdit: string
    quickViewEnabled: boolean
    quickViewState: string
    root: string // The parent state shown in the graph
    selected: Selected // The set of states that are selected in the graph
    selectedActive: string // The active member of the selection
    showMiniMap: boolean
    useCookbookPositions: boolean
    viewport: { x: number, y: number, zoom: number }
    activeGroup: string
    hiddenGroupValues: {[value: string]: boolean}
    splitGroupsOnBranching: boolean
    touchMode: boolean
}
export interface ILocalizationData {
    exportJson?: boolean
}
export interface IScriptData {
    scriptMode: string
    sequencePageBreak: boolean
    exportDocX?: boolean
}
export interface ISheetModeData
{
    selectedCell: { field: string, row: string }
    selection?: GridSelectionModel
    sheetId?: string
    expanded: { [id: string]: boolean }
    groupedViewEnabled: boolean
}
export interface IWriterData {
}

export type ModeData = (IAssetsData | IGraphData | ILocalizationData | ISheetModeData | IScriptData | IWriterData);
export type Selected = string[];
export type SetModeData = (data: ModeData) => void;
export type UpdateFocus = (focus: string) => void;
export type UpdateModeData = (data: Partial<ModeData>) => void;
interface EditorState {
    dirty: boolean
    exception?: ExceptionType
    focus: string
    mode: string
    modeData: {[mode: string]: ModeData}
    project: string
    searchText?: string
    title: string
    helpAvailable: boolean
    userId?: string
    activeGroup?: string
    writerMode: "network" | "grammar"
}

const getInitialModeDataState = (mode: string): {[member: string]: any } => {
    const defaults: {[key: string]: {[member: string]: any}} = {
        graph: {
            events: [],
            graphLayout: localStorage.getItem("graph.graphLayout") || "script",
            lockedStates: localStorage.getItem("graph.lockedStates") !== "false",
            quickViewEnabled: localStorage.getItem("graph.quickViewEnabled") !== "false",
            showMiniMap: localStorage.getItem("graph.showMiniMap") !== "false",
            useCookbookPositions: localStorage.getItem("graph.useCookbookPositions") !== "false",
            viewport: { x:0, y:0, zoom: 1},
            activeGroup: localStorage.getItem("graph.activeGroup") !== "",
            hiddenGroupValues: {},
            splitGroupsOnBranching: localStorage.getItem("graph.splitGroupsOnBranching") !== "false",
            touchMode: false
        },
        sheet: {
            selectedCell: { field: "", row: "" },
            expanded: {},
            groupedViewEnabled: localStorage.getItem("sheet.groupedViewEnabled") !== "false",
        },
        script: {
            scriptMode: localStorage.getItem("script.scriptMode") ?? "paged",
            sequencePageBreak: localStorage.getItem("script.sequencePageBreak") !== "false"
        }
    };

    return defaults[mode] || {};
};

const initialState = {
    dirty: false,
    focus: "",
    helpAvailable: true,
    mode: "",
    modeData: {},
    project: "",
    quickViewEdit: undefined,
    quickViewState: undefined,
    title: "",
    writerMode: "network"
} as EditorState;

const slice = createSlice({
    name: "editor",
    initialState,
    reducers: {
        removeException(state) {
            state.exception = undefined;
        },
        setModeData(state, action: PayloadAction<ModeData>) {
            state.modeData[state.mode] = action.payload;
        },
        updateException(state, action: PayloadAction<ExceptionType>) {
            state.exception = action.payload;
        },
        updateFocus(state, action: PayloadAction<string>) {
            state.focus = action.payload;
        },
        updateWriterMode(state, action: PayloadAction<"network" | "grammar">) {
            state.writerMode = action.payload;
        },
        updateMode(state, action: PayloadAction<string>) {
            if (state.mode !== action.payload) {
                state.mode = action.payload;
                state.modeData[state.mode] = state.modeData[state.mode] ?? getInitialModeDataState(action.payload);
            }
        },
        updateModeData(state, action: PayloadAction<Partial<ModeData>>) {
            const initialModeData = getInitialModeDataState(state.mode);

            for (let key of Object.keys(initialModeData)) {
                const value = action.payload[key as keyof ModeData] as string | boolean;

                if (value !== undefined) {
                    localStorage.setItem(`${state.mode}.${key}`, value.toString());
                }
            }
            state.modeData[state.mode] = {
                ...state.modeData[state.mode],
                ...action.payload
            };
        },
        updateHelpAvailable(state, action: PayloadAction<boolean>) {
            state.helpAvailable = action.payload;
        },
        updateProject(state, action: PayloadAction<string>) {
            state.project = action.payload;
        },
        updateSearchText(state, action: PayloadAction<string>) {
            state.searchText = action.payload || undefined
        },
        updateTitle(state, action: PayloadAction<string>) {
            state.title = action.payload
        },
        updateUserId(state, action: PayloadAction<string>) {
            state.userId = action.payload;        
        }
    }
});

export const {removeException, setModeData, updateException, updateFocus, updateHelpAvailable, updateMode,
    updateModeData, updateProject, updateSearchText, updateTitle, updateUserId, updateWriterMode} = slice.actions;
export default slice.reducer;